/**
 * Lazy wrapper cho @sentry/browser.
 *
 * Mục tiêu: giữ SDK Sentry (~257KB) RA KHỎI initial bundle. Trước đây Sentry được import
 * tĩnh ở ~20 file (apollo, crashReporter, ErrorBoundary...) nên dù init đã defer, code SDK
 * vẫn nằm trong vendor initial -> parse trên main thread lúc bootstrap (góp phần TBT cao).
 *
 * Cách hoạt động: import động @sentry/browser ở lần gọi đầu tiên (tạo async chunk riêng).
 * - Nếu SDK đã load (sentryInstance != null) -> gọi ĐỒNG BỘ (giữ đúng scope context).
 * - Nếu chưa -> kích hoạt load rồi gọi khi xong.
 *
 * Lưu ý quan trọng về withScope: pattern dùng là
 *     withScope(scope => { scope.setExtra(...); captureException(e); })
 *   Khi callback chạy thì SDK đã load xong (sentryInstance set) nên captureException bên
 *   trong chạy đồng bộ -> nằm đúng trong scope. Không mất context.
 *
 * API tái xuất khớp đúng các chỗ đang dùng: init, captureException, captureMessage,
 * withScope, addBreadcrumb, showReportDialog, Severity.
 */

type SentryModule = typeof import('@sentry/browser');

let sentryPromise: Promise<SentryModule> | null = null;
let sentryInstance: SentryModule | null = null;

const load = (): Promise<SentryModule> => {
  if (!sentryPromise) {
    sentryPromise = import('@sentry/browser').then((mod) => {
      sentryInstance = mod;
      return mod;
    });
  }
  return sentryPromise;
};

// Severity là enum string của Sentry — tái tạo local để KHỎI phải import SDK đồng bộ
// (các call site dùng Sentry.Severity.Error... ngay khi gọi captureException).
export const Severity = {
  Fatal: 'fatal',
  Critical: 'critical',
  Error: 'error',
  Warning: 'warning',
  Log: 'log',
  Info: 'info',
  Debug: 'debug',
} as const;

type InitOptions = Parameters<SentryModule['init']>[0];
let initialized = false;
export const isInitialized = (): boolean => initialized;

export const init = (options: InitOptions | ((S: SentryModule) => InitOptions)): void => {
  load().then((S) => {
    S.init(typeof options === 'function' ? options(S) : options);
    initialized = true;
  });
};

export const captureException = (exception: any, captureContext?: any): void => {
  if (sentryInstance) {
    (sentryInstance.captureException as any)(exception, captureContext);
    return;
  }
  load().then((S) => (S.captureException as any)(exception, captureContext));
};

export const captureMessage = (message: any, captureContext?: any): void => {
  if (sentryInstance) {
    (sentryInstance.captureMessage as any)(message, captureContext);
    return;
  }
  load().then((S) => (S.captureMessage as any)(message, captureContext));
};

export const withScope = (callback: (scope: any) => void): void => {
  if (sentryInstance) {
    sentryInstance.withScope(callback);
    return;
  }
  load().then((S) => S.withScope(callback));
};

export const addBreadcrumb = (breadcrumb: any): void => {
  if (sentryInstance) {
    sentryInstance.addBreadcrumb(breadcrumb);
    return;
  }
  load().then((S) => S.addBreadcrumb(breadcrumb));
};

// Trả undefined khi SDK chưa load — mọi call site đều optional-chain
// (getCurrentHub()?.getScope()?...) nên an toàn; không kích hoạt load vì
// hub chỉ có nghĩa khi SDK + init đã chạy xong.
export const getCurrentHub = (): any => {
  return sentryInstance ? sentryInstance.getCurrentHub() : undefined;
};

export const showReportDialog = (options?: any): void => {
  if (sentryInstance) {
    sentryInstance.showReportDialog(options);
    return;
  }
  load().then((S) => S.showReportDialog(options));
};
